import React, { Suspense, useEffect, useState, createContext } from "react";
import { useRoutes, Navigate, useLocation } from "react-router-dom";
import { setCookie } from "./utils/setCookie";
import "./App.css";
import ToTop from "./components/other/ToTop";
import loadingImg from "./assets/images/pub/loading.svg";

// 创建主题上下文
export const ThemeContext = createContext();
export const FontSizeContext = createContext();

const AboutUs = React.lazy(() => import("./pages/aboutUs"));
const BigDuck = React.lazy(() => import("./pages/bigDuck"));
const LoadingBD = React.lazy(() => import("./pages/loadingBD"));
const NewPost = React.lazy(() => import("./pages/newPost"));
const PostDetails = React.lazy(() => import("./pages/postDetails"));
const Home = React.lazy(() => import("./pages/home"));
const Search = React.lazy(() => import("./pages/search"));
const LoadingUrl = React.lazy(() => import("./pages/loadingUrl"));
const Auth = React.lazy(() => import("./pages/auth"));
const Notify = React.lazy(() => import("./pages/notify"));
const PersonalSet = React.lazy(() => import("./pages/personalSet"));
const Personal = React.lazy(() => import("./pages/personalSet/personal"));
const OfficialAccount = React.lazy(() => import("./pages/officialAccount"));
const ActiveInfo = React.lazy(() => import("./pages/activeInfo"));
const Download = React.lazy(() => import("./pages/download"));
const RichMan = React.lazy(() => import("./pages/richMan"));
const Popular = React.lazy(() => import("./pages/popular"));
const WWW = React.lazy(() => import("./pages/www"));
const Appeal = React.lazy(() => import("./pages/appeal"));

const App = () => {
  const location = useLocation();
  // 添加主题状态
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [fontSize, setFontSize] = useState(localStorage.getItem("fontSize") || "normal");

  const [show360Modal, setShow360Modal] = useState(false);

  // 添加字体大小切换函数
  const toggleFontSize = () => {
    const newFontSize = fontSize === "normal" ? "big" : "normal";
    setFontSize(newFontSize);
    localStorage.setItem("fontSize", newFontSize);
    document.body.setAttribute("data-font-size", newFontSize);
  };

  // 添加主题切换函数
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.body.setAttribute("data-theme", newTheme);
  };

  // 检测屏幕宽度，移动端跳转
  useEffect(() => {
    const checkScreenWidth = () => {
      const isMobile = window.innerWidth < 768;
      const isAlreadyMobile = window.location.hostname === "m.kdslife.com";
      if (isMobile && !isAlreadyMobile) {
        const currentUrl = window.location.href;
        const threadDetailMatch = currentUrl.match(
          /\/new\/thread\/detail\?.*tid=(\d+)/
        );
        const downloadMatch = currentUrl.match(
          /\/new\/download\//
        );
        if (threadDetailMatch) {
          const tid = threadDetailMatch[1];
          window.location.href = `https://h5.kdslife.com/detail/${tid}`;
        } else if (downloadMatch) {
          window.location.href = `https://h5.kdslife.com/download`;
        } else {
          window.location.href = "https://h5.kdslife.com";
        }
      }
    };
    checkScreenWidth();
    window.addEventListener("resize", checkScreenWidth);
    return () => {
      window.removeEventListener("resize", checkScreenWidth);
    };
  }, []);

  useEffect(() => {
    // 百度统计：推送页面路径
    if (window._hmt) {
      window._hmt.push(["_trackPageview", location.pathname]);
    }

    // Google Analytics：发送页面浏览事件
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    console.log(location.pathname, "页面统计");
  }, [location]);
  useEffect(() => {
    // 回旧版，每次进入新地址会让它过期
    setCookie("return_to_old", "", -1);
    document.body.setAttribute("data-theme", theme);
    document.body.setAttribute("data-font-size", fontSize);
  }, []);


// 识别“360极速浏览器”并轻提示更新版本
  useEffect(() => {
    const is360ExtremeBrowser = () => {
      const ua = (navigator.userAgent || "").toLowerCase();
      return ua.includes("chrome/86");
    };

    try {
      if (is360ExtremeBrowser()) {
        // 获取今日日期字符串，格式：YYYY-MM-DD
        const today = new Date().toISOString().slice(0, 10);
        const lastPromptDate = localStorage.getItem("360PromptDate");
        // 如果今天还没弹过，则弹窗
        if (lastPromptDate !== today) {
          setShow360Modal(true);
        }
      }
    } catch (e) {
      // 兼容性兜底，不影响页面其他逻辑
    }
  }, []);


  const routes = useRoutes([
    // {
    //   path: '/new/www',
    //   element: <WWW />,
    // },

    {
      path: "/new/thread",
      children: [
        { index: true, element: <Navigate to="post" replace /> }, // 默认重定向到post
        { path: "post", element: <NewPost /> },
        { path: "detail", element: <PostDetails /> },
      ],
    },
    {
      path: "/new/aboutUs",

      element: <AboutUs />,
    },
    {
      path: "/new/bigDuck",
      children: [
        { index: true, element: <Navigate to="home" replace /> }, // 默认重定向到home
        { path: "home", element: <BigDuck /> },
        { path: "loading", element: <LoadingBD /> },
      ],
    },
    {
      path: "/new/home",
      element: <Home />,
    },
    {
      path: "/new/search",
      element: <Search />,
    },
    {
      path: "/new/loadingUrl",
      element: <LoadingUrl />,
    },
    {
      path: "/new/auth",
      children: [
        { index: true, element: <Navigate to="login" replace /> }, // 默认重定向到login
        { path: "login", element: <Auth type="login" /> },
        { path: "register", element: <Auth type="register" /> },
        { path: "forgotPassword", element: <Auth type="forgot-password" /> },
        { path: "forgotAccount", element: <Auth type="forgot-account" /> },
        { path: "wxLogin", element: <Auth type="wx-login" /> },
        { path: "binding", element: <Auth type="binding" /> },
        { path: "bindingPhone", element: <Auth type="binding-phone" /> },
      ],
    },
    {
      path: "/new/notify",
      element: <Notify />,
    },
    {
      path: "*",
      element: <Navigate to="/new/home" replace />,
    },

    {
      path: "/new/personalSet",
      element: <PersonalSet />,
    },
    {
      path: "/new/officialAccount",
      element: <OfficialAccount />,
    },
    {
      path: "/new/activeInfo",
      element: <ActiveInfo />,
    },
    {
      path: "/new/download",
      element: <Download />,
    },
    {
      path:"/new/richMan",
      element:<RichMan />
    },
    {
      path:"/new/popular",
      element:<Popular />
    },
    {
      path:"/new/appeal",
      element:<Appeal />
    },
  ]);

  const loadingPage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <img src={loadingImg} alt="loading" />
    </div>
  );

  // 路由判断
  const getToTopType = (location) => {
    const returnMinusOnePaths = [
      "bigDuck/loading",
      "new/officialAccount",
      "new/activeInfo",
      "new/download",
      "new/richMan",
      "new/popular",
      "new/www",
      "new/appeal",
    ];
    if (returnMinusOnePaths.some((path) => location.pathname.includes(path)))
      return -1;

    const matchPaths = [
      "bigDuck/home",
      "new/aboutUs",
      "new/search",
      "new/personal",
      "new/personalSet",
      "new/auth",
      "new/notify",
    ];
    return matchPaths.some((path) => location.pathname.includes(path)) ? 0 : 1;
  };


    const NativeConfirmModal = ({
    visible,
    title,
    message,
    confirmText = "确认",
    cancelText = "取消",
    onConfirm,
    onClose,
  }) => {
    if (!visible) return null;


    const overlayStyle = {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    };
    const modalStyle = {
      background: "#fff",
      borderRadius: "12px",
      padding: "16px",
      width: "min(88vw, 360px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    };
    const titleStyle = {
      fontSize: "16px",
      fontWeight: 600,
      color: "#1f1f1f",
      marginBottom: "8px",
      display: "block",
    };
    const contentStyle = {
      fontSize: "14px",
      color: "#595959",
      lineHeight: 1.6,
      margin: "8px 0 16px",
    };
    const btnGroupStyle = {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    };
    const primaryBtnStyle = {
      background: "#1677ff",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      padding: "8px 12px",
      cursor: "pointer",
    };
    const cancelBtnStyle = {
      background: "#fff",
      color: "#1f1f1f",
      border: "1px solid #d9d9d9",
      borderRadius: "8px",
      padding: "8px 12px",
      cursor: "pointer",
    };


    return (
      <div
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {typeof title === "string" ? (
            <span style={titleStyle}>{title}</span>
          ) : (
            title
          )}
          {message && (
            <div
              style={contentStyle}
              dangerouslySetInnerHTML={{ __html: message }}
            />
          )}
          <div style={btnGroupStyle}>
            <button style={primaryBtnStyle} onClick={onConfirm}>
              {confirmText}
            </button>
            <button style={cancelBtnStyle} onClick={onClose}>
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <FontSizeContext.Provider value={{ fontSize, toggleFontSize }}>
      <Suspense fallback={loadingPage}>
        {" "}
        {/* 当组件正在加载时显示加载指示器 */}
        {routes}
        <ToTop
          type={getToTopType(location)}
          currentTheme={theme}
          onThemeChange={toggleTheme}
          currentFontSize={fontSize}
          onFontSizeChange={toggleFontSize}
        />
                  <NativeConfirmModal
            visible={show360Modal}
            title="浏览器版本过低，可能影响使用体验"
            message='<div>检测到你正在使用旧版本的 360 极速浏览器，可能无法完全兼容 <span style="color: #0078D4;">www.kdslife.com</span>，导致页面显示异常或部分功能不可用。
建议前往 360 官方网站升级浏览器，以获得更安全、流畅的使用体验。</div>'
            confirmText="前往升级"
            cancelText="继续使用当前版本"
            onConfirm={() => {
              window.open("https://browser.360.cn/", "_blank");
              localStorage.setItem(
                "360PromptDate",
                new Date().toISOString().slice(0, 10)
              );
              setShow360Modal(false);
            }}
            onClose={() => {
              localStorage.setItem(
                "360PromptDate",
                new Date().toISOString().slice(0, 10)
              );
              setShow360Modal(false);
            }}
          />
      </Suspense>
      </FontSizeContext.Provider>
    </ThemeContext.Provider>
  );
};

export default App;
